<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WhatsAppTextTemplate from 'dashboard/components-next/template-preview/WhatsAppTextTemplate.vue';
import {
  buildTemplateComponents,
  emptyTemplateForm,
  formatTemplateLanguage,
  templateToForm,
  templateVariables,
} from './templateUtils';

const props = defineProps({
  inboxes: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const LANGUAGES = ['he', 'en', 'en_US', 'ar', 'ru'];
const CATEGORIES = ['MARKETING', 'UTILITY'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
const TEMPLATE_NAME_PATTERN = /^[a-z0-9_]+$/;
const BODY_MAX_LENGTH = 1024;

const { t } = useI18n();

const dialogRef = ref(null);
const editingTemplate = ref(null);
const isSaving = ref(false);
const form = reactive(emptyTemplateForm());

const inboxOptions = computed(() =>
  props.inboxes.map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const languageOptions = LANGUAGES.map(language => ({
  value: language,
  label: formatTemplateLanguage(language),
}));
const categoryOptions = computed(() =>
  CATEGORIES.map(category => ({
    value: category,
    label: t(`WHATSAPP_TEMPLATE_MGMT.FORM.CATEGORIES.${category}`),
  }))
);
const buttonTypeOptions = computed(() =>
  BUTTON_TYPES.map(type => ({
    value: type,
    label: t(`WHATSAPP_TEMPLATE_MGMT.FORM.BUTTON_TYPES.${type}`),
  }))
);

const headerVariables = computed(() => templateVariables(form.header));
const bodyVariables = computed(() => templateVariables(form.body));

const nameError = computed(() =>
  form.name && !TEMPLATE_NAME_PATTERN.test(form.name)
    ? t('WHATSAPP_TEMPLATE_MGMT.FORM.NAME_HINT')
    : ''
);

const isValid = computed(
  () =>
    form.inboxId &&
    TEMPLATE_NAME_PATTERN.test(form.name) &&
    form.body.trim() &&
    (!headerVariables.value.length || form.headerExample.trim()) &&
    bodyVariables.value.every((_, index) => form.bodyExamples[index]?.trim()) &&
    form.buttons.every(
      button =>
        button.text.trim() &&
        (button.type === 'QUICK_REPLY' || button.value.trim())
    )
);

const open = (template = null) => {
  editingTemplate.value = template;
  Object.assign(
    form,
    template
      ? templateToForm(template)
      : { ...emptyTemplateForm(), inboxId: props.inboxes[0]?.id }
  );
  dialogRef.value.open();
};

const addButton = () =>
  form.buttons.push({ type: 'QUICK_REPLY', text: '', value: '' });

const save = async () => {
  isSaving.value = true;
  try {
    const components = buildTemplateComponents(form);
    if (editingTemplate.value) {
      await InboxesAPI.updateMessageTemplate(
        form.inboxId,
        editingTemplate.value.id,
        components
      );
    } else {
      await InboxesAPI.createMessageTemplate(form.inboxId, {
        name: form.name,
        language: form.language,
        category: form.category,
        components,
      });
    }
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.FORM.SAVED'));
    dialogRef.value.close();
    emit('saved');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('WHATSAPP_TEMPLATE_MGMT.FORM.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      editingTemplate
        ? $t('WHATSAPP_TEMPLATE_MGMT.FORM.EDIT_TITLE', {
            name: editingTemplate.name,
          })
        : $t('WHATSAPP_TEMPLATE_MGMT.FORM.CREATE_TITLE')
    "
    :description="$t('WHATSAPP_TEMPLATE_MGMT.FORM.REVIEW_NOTE')"
    :confirm-button-label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.SUBMIT')"
    :disable-confirm-button="!isValid"
    :is-loading="isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.FORM.INBOX') }}
          </span>
          <ComboBox
            v-model="form.inboxId"
            :options="inboxOptions"
            :disabled="Boolean(editingTemplate)"
          />
        </div>
        <Input
          v-model="form.name"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.NAME')"
          :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.FORM.NAME_PLACEHOLDER')"
          :message="nameError"
          :message-type="nameError ? 'error' : 'info'"
          :disabled="Boolean(editingTemplate)"
        />
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.LANGUAGE') }}
          </span>
          <ComboBox
            v-model="form.language"
            :options="languageOptions"
            :display-label="formatTemplateLanguage(form.language)"
            :disabled="Boolean(editingTemplate)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.CATEGORY') }}
          </span>
          <ComboBox
            v-model="form.category"
            :options="categoryOptions"
            :display-label="form.category"
            :disabled="Boolean(editingTemplate)"
          />
        </div>
      </div>

      <Input
        v-model="form.header"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.HEADER')"
      />
      <Input
        v-if="headerVariables.length"
        v-model="form.headerExample"
        :label="
          $t('WHATSAPP_TEMPLATE_MGMT.FORM.EXAMPLE', {
            variable: headerVariables[0],
          })
        "
      />

      <TextArea
        v-model="form.body"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.BODY')"
        :message="$t('WHATSAPP_TEMPLATE_MGMT.FORM.BODY_HINT')"
        :max-length="BODY_MAX_LENGTH"
        show-character-count
        auto-height
      />
      <Input
        v-for="(variable, index) in bodyVariables"
        :key="variable"
        v-model="form.bodyExamples[index]"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.EXAMPLE', { variable })"
      />

      <Input
        v-model="form.footer"
        :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.FOOTER')"
      />

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.FORM.BUTTONS') }}
        </span>
        <div
          v-for="(button, index) in form.buttons"
          :key="index"
          class="flex items-center gap-2"
        >
          <ComboBox
            v-model="button.type"
            :options="buttonTypeOptions"
            class="w-40 shrink-0"
          />
          <Input
            v-model="button.text"
            :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.FORM.BUTTON_TEXT')"
            class="flex-1"
          />
          <Input
            v-if="button.type !== 'QUICK_REPLY'"
            v-model="button.value"
            :placeholder="
              $t(`WHATSAPP_TEMPLATE_MGMT.FORM.BUTTON_VALUES.${button.type}`)
            "
            class="flex-1"
          />
          <Button
            icon="i-lucide-trash-2"
            color="slate"
            size="sm"
            type="button"
            :aria-label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.REMOVE_BUTTON')"
            @click="form.buttons.splice(index, 1)"
          />
        </div>
        <Button
          :label="$t('WHATSAPP_TEMPLATE_MGMT.FORM.ADD_BUTTON')"
          icon="i-lucide-plus"
          color="slate"
          size="sm"
          type="button"
          class="self-start"
          @click="addButton"
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.TITLE') }}
        </span>
        <WhatsAppTextTemplate
          :message="{
            title: form.header,
            content: form.body,
            footer: form.footer,
          }"
        />
      </div>
    </div>
  </Dialog>
</template>
